"use client";
import React, { useCallback, useMemo } from "react";
import { LearningAndDevelopmentPostQueryFilter } from "@onair/services/learning-development";
import { useGetLearningAndDevelopmentPostsInfiniteQuery } from "@onair/operations";
import CardPost from "../card-post";
import CardPostVertical from "../card-post-vertical";
import {
	useSearchPostsStore,
	useCategoryFilterStore,
} from "../../hooks/use-category-filter";
import { htmlToText } from "@onair/utils/html-to-text";
import { Button, Typography } from "@mui/material";
import EmptyData from "@onair/ui/empty-data";
interface PostListContainerProps {
	className?: string;
	defaultQueryFilter: LearningAndDevelopmentPostQueryFilter;
}
const PostListContainer: React.FC<PostListContainerProps> = ({
	defaultQueryFilter,
}) => {
	const textSearch = useSearchPostsStore((state) => state.textSearch);
	const categoriesFilter = useCategoryFilterStore((state) => state.categories);

	const queryFilter = useMemo(() => {
		const { categories, ...restFilter } = defaultQueryFilter;
		let queryFilterCondition: LearningAndDevelopmentPostQueryFilter = {
			...restFilter,
		};
		if (
			categoriesFilter.length === categories?.length ||
			!categoriesFilter.length
		) {
			queryFilterCondition = {
				...queryFilterCondition,
				categories: categories,
			};
		} else {
			queryFilterCondition = {
				...queryFilterCondition,
				categories: categoriesFilter,
			};
		}
		if (textSearch) {
			queryFilterCondition = {
				...queryFilterCondition,
				searchText: textSearch,
			};
		}
		return queryFilterCondition;
	}, [defaultQueryFilter, categoriesFilter, textSearch]);

	const {
		data: postListPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useGetLearningAndDevelopmentPostsInfiniteQuery(queryFilter);

	const postList = [...(postListPage?.pages || [])]
		.flatMap((page) => page.data)
		.filter((item) => !!item);

	const firstPost = postList[0];
	const secondTwoPosts = [...postList].splice(1, 2);
	const restPosts = [...postList].splice(3);

	const isShowButtonLoadmore = useMemo(() => {
		const pageCount = postListPage?.pages[0].meta?.pagination?.pageCount || 1;
		const pageParams = (postListPage?.pageParams || [1]) as number[];
		const lastPage = pageParams[pageParams.length - 1];

		return lastPage < pageCount;
	}, [postListPage]);

	const handleFetchNextPage = useCallback(() => {
		if (!isFetchingNextPage) {
			fetchNextPage();
		}
	}, []);

	if (!postList.length)
		return (
			<div className="empty-list">
				<Typography>
					<EmptyData
						title=""
						description="Chúng tôi không tìm thấy lựa chọn nào phù hợp với yêu cầu của bạn!."
					/>
				</Typography>
			</div>
		);
	return (
		<div className="blog-list-container">
			{firstPost ? (
				<CardPostVertical
					authorAvatarUrl={firstPost.author?.avatar?.url}
					authorName={firstPost.author?.username}
					createdAt={firstPost.createdAt}
					title={firstPost.title}
					slug={firstPost.slug}
					thunmbnailUrl={firstPost.banner?.url}
					description={
						firstPost.content ? htmlToText(firstPost.content).slice(0, 240) : ""
					}
					//@ts-ignore
					categories={firstPost.post_categories?.map((cat) => ({
						id: cat.id,
						//@ts-ignore
						title: cat?.title || "",
					}))}
				/>
			) : null}
			<div className="h-12"></div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-12 mb-12">
				{secondTwoPosts.map((post) => (
					<CardPost
						key={post.id}
						authorAvatarUrl={post.author?.avatar?.url}
						authorName={post.author?.username}
						createdAt={post.createdAt}
						title={
							<h3 className="text-xl lg:text-3xl font-bold line-clamp-2 leading-normal">
								{post.title}
							</h3>
						}
						thunmbnailUrl={post.banner?.url}
						slug={post.slug}
					/>
				))}
			</div>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12">
				{restPosts.map((post) => (
					<CardPost
						key={post.id}
						authorAvatarUrl={post.author?.avatar?.url}
						authorName={post.author?.username}
						createdAt={post.createdAt}
						title={post.title}
						thunmbnailUrl={post.banner?.url}
						slug={post.slug}
					/>
				))}
			</div>
			{isShowButtonLoadmore && (
				<div className="button-loadmore text-center pt-12">
					<Button
						variant="outlined"
						size="large"
						className="min-w-[120px]"
						onClick={handleFetchNextPage}
					>
						{isFetchingNextPage ? "Đang tải..." : "Tải thêm"}
					</Button>
				</div>
			)}
		</div>
	);
};
export default PostListContainer;
